import { useDispatch, useSelector } from '@tabeeb/store'
import { useCallback, useMemo } from 'react'

import { getIsCurrentUserPresenter } from '@tabeeb/shared/content/selectors'
import { muteAudio, muteRemoteAudio } from '@tabeeb/modules/presentationToolbar/actions/muteAudio'

import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { useHotkey } from '@collaborate-health/uikit/hooks'
import { ToggleMicrophone } from '@tabeeb/shared/constants/keyboardShortcuts'
import { VideoCallUser } from '@tabeeb/modules/../users/types'
import { getIsMuteAudioInProgress } from '@tabeeb/modules/presentationToolbar/selectors'

import { AUDIO_MUTED } from '../constants'
import { getIsCallPreviewOpen, getIsUserAudioPlaying, getSelectedUser } from '../selectors'

interface UseUserMicrophoneProps {
  user: VideoCallUser | null
  enableHotkey?: boolean
}

const useUserMicrophone = ({ user, enableHotkey = false }: UseUserMicrophoneProps) => {
  const dispatch = useDispatch()

  const currentUserId = useSelector<number>(getCurrentUserId)
  const isPresenter = useSelector(getIsCurrentUserPresenter)
  const selectedCallUser = useSelector(getSelectedUser)
  const isMuted = useSelector((state) => !getIsUserAudioPlaying(state, { Id: user?.id! }))
  const isMuteInProgress = useSelector(getIsMuteAudioInProgress)
  const isCallPreviewOpen = useSelector<boolean>(getIsCallPreviewOpen)

  const handleClick = useCallback(() => {
    if (!user || isMuteInProgress === true || (!user.isOnline && !isCallPreviewOpen)) {
      return
    }

    if (user.id === currentUserId) {
      dispatch(muteAudio())
      if (isCallPreviewOpen) {
        localStorage.setItem(AUDIO_MUTED, JSON.stringify(!isMuted))
      }
    } else if (isPresenter) {
      dispatch(muteRemoteAudio(user.id))
    }
  }, [currentUserId, dispatch, isCallPreviewOpen, isMuteInProgress, isMuted, isPresenter, user])

  const isHotkeyEnabled = useMemo(() => {
    return (user && user.id === currentUserId && enableHotkey) || false
  }, [currentUserId, enableHotkey, user])

  useHotkey(
    ToggleMicrophone,
    () => {
      handleClick()
    },
    { preventDefault: true, enabled: isHotkeyEnabled }
  )

  return useMemo(
    () => ({ handleClick, isPresenter, currentUserId, isMuted, selectedCallUser }),
    [currentUserId, handleClick, isMuted, isPresenter, selectedCallUser]
  )
}

export default useUserMicrophone

